import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './Dashboard.css';

const Dashboard = () => {
  const { user, fetchUser } = useAuth();
  const [balance, setBalance] = useState(0);
  const [devices, setDevices] = useState([]);
  const [dailyRate, setDailyRate] = useState(4.0);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('home');
  const [deviceName, setDeviceName] = useState('');
  const [showManualInstructions, setShowManualInstructions] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportMessage, setSupportMessage] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportFile, setSupportFile] = useState(null);
  const [supportFilePreview, setSupportFilePreview] = useState(null);
  const supportMessagesEndRef = React.useRef(null);
  
  // LTE traffic state
  const [lteTrafficUsedGB, setLteTrafficUsedGB] = useState(0);
  const [lteTrafficTotalGB, setLteTrafficTotalGB] = useState(30);
  const [lteTrafficRemainingGB, setLteTrafficRemainingGB] = useState(30);
  const [ltePurchaseLoading, setLtePurchaseLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Reload devices when devices tab is active
  useEffect(() => {
    if (activeTab === 'devices') {
      loadDevices();
    }
  }, [activeTab]);

  useEffect(() => {
    if (showSupportDialog) {
      loadSupportMessages();
      const interval = setInterval(loadSupportMessages, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [showSupportDialog]);

  // Scroll to bottom when messages change or dialog opens
  useEffect(() => {
    if (showSupportDialog && supportMessages.length > 0) {
      setTimeout(() => {
        if (supportMessagesEndRef.current) {
          supportMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [supportMessages, showSupportDialog]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [subscriptionRes, userRes, devicesRes] = await Promise.all([
        axios.get('/api/subscription/my'),
        axios.get('/api/user/me'),
        axios.get('/api/user/devices').catch(() => ({ data: { devices: [] } }))
      ]);
      
      // Get balance and LTE traffic from user data
      if (userRes.data) {
        setBalance(userRes.data.balance || 0.0);
        
        // Set LTE traffic data
        setLteTrafficUsedGB(userRes.data.lteTrafficUsedGB || 0);
        setLteTrafficTotalGB(userRes.data.lteTrafficTotalGB || 30);
        setLteTrafficRemainingGB(userRes.data.lteTrafficRemainingGB || 30);
        
        fetchUser();
      }
      
      // Calculate rates (mock data for now)
      if (subscriptionRes.data) {
        setDailyRate(4.0);
      }

      // Load devices - ensure it's always an array
      if (devicesRes.data && devicesRes.data.devices) {
        const devicesArray = Array.isArray(devicesRes.data.devices) 
          ? devicesRes.data.devices 
          : [];
        setDevices(devicesArray);
      } else {
        setDevices([]);
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadData();
    fetchUser();
  };

  const handleAddDevice = () => {
    if (deviceName.trim()) {
      // Device will be added when user connects with new device
      setDeviceName('');
      setActiveTab('devices');
      // Reload devices after a short delay
      setTimeout(() => {
        loadData();
      }, 2000);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await axios.get('/api/user/devices');
      if (response.data && response.data.devices) {
        const devicesArray = Array.isArray(response.data.devices) 
          ? response.data.devices 
          : [];
        setDevices(devicesArray);
      } else {
        setDevices([]);
      }
    } catch (err) {
      console.error('Error loading devices:', err);
      setDevices([]);
    }
  };

  const handlePurchaseLte = async (amountGb) => {
    try {
      setLtePurchaseLoading(true);
      setError('');
      
      const response = await axios.post('/api/user/purchase-lte', { amountGb });
      
      if (response.data.success) {
        alert(`Успешно куплено ${amountGb} ГБ LTE трафика!`);
        // Refresh data to update balance and traffic
        await loadData();
        await fetchUser();
        setActiveTab('home');
      }
    } catch (err) {
      console.error('Error purchasing LTE:', err);
      
      if (err.response?.data?.error === 'INSUFFICIENT_BALANCE') {
        const needToTopUp = err.response.data.needToTopUp;
        if (window.confirm(`Недостаточно средств. Не хватает ${needToTopUp.toFixed(2)} ₽. Хотите пополнить баланс?`)) {
          setActiveTab('topup');
        }
      } else {
        setError(err.response?.data?.message || 'Ошибка покупки трафика');
      }
    } finally {
      setLtePurchaseLoading(false);
    }
  };

  const handleTopUp = async (amount) => {
    try {
      setError('');
      
      // Create payment
      const paymentResponse = await axios.post('/api/payment/create', { amount });
      
      if (!paymentResponse.data.success) {
        throw new Error('Не удалось создать платеж');
      }

      const { publicId, invoiceId, description, email, phone } = paymentResponse.data;

      // Check if CloudPayments widget is available
      if (typeof window !== 'undefined' && window.cp && window.cp.CloudPayments) {
        const widget = new window.cp.CloudPayments();
        
        // Don't show our loader - CloudPayments widget has its own loader
        
        widget.pay('charge', {
          publicId: publicId,
          description: description,
          amount: amount,
          currency: 'RUB',
          invoiceId: invoiceId,
          email: email,
          phone: phone,
          accountId: user?.id?.toString(),
          skin: 'modern',
          data: {
            userId: user?.id
          }
        }, {
          onSuccess: async (options) => {
            // Payment successful
            try {
              // Wait a bit for webhook to process
              setTimeout(async () => {
                // Refresh user data to get updated balance
                await loadData();
                await fetchUser();
                setActiveTab('home');
                setError('');
                alert('Платеж успешно выполнен!');
              }, 2000);
            } catch (err) {
              console.error('Error refreshing data:', err);
            }
          },
          onFail: (reason, options) => {
            setError('Ошибка при оплате: ' + reason);
            console.error('Payment failed:', reason, options);
          },
          onComplete: (paymentResult, options) => {
            // Payment completed (success or fail)
          }
        });
      } else {
        throw new Error('CloudPayments виджет не загружен. Пожалуйста, обновите страницу.');
      }
    } catch (err) {
      console.error('Error in handleTopUp:', err);
      setError(err.response?.data?.message || err.message || 'Ошибка пополнения баланса');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div>Загрузка...</div>
      </div>
    );
  }

  const renderContent = () => {
    if (activeTab === 'devices') {
  return (
        <>
          <div className="dashboard-header">
            <div className="header-left">
              <button className="header-icon-btn" onClick={() => setActiveTab('home')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"></path>
                </svg>
              </button>
              <div className="header-title">
                <h1>Мои устройства</h1>
                <p>NaixVPN</p>
            </div>
            </div>
          </div>
          <div className="devices-section">
            {!Array.isArray(devices) || devices.length === 0 ? (
              <div className="devices-empty">
                <svg className="device-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.5, marginBottom: '1rem' }}>
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>Нет подключенных устройств</p>
                <p style={{ color: 'rgba(255, 255, 255, 0.4)', textAlign: 'center', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Подключите устройство через VPN, чтобы оно отобразилось здесь
                </p>
              </div>
            ) : (
              <div className="devices-list">
                {Array.isArray(devices) && devices.map((device, index) => (
                  <div key={device.hwid || index} className="device-card">
                    <div className="device-info">
                      <svg className="device-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                      </svg>
                      <div style={{ flex: 1 }}>
                        <div className="device-name">
                          {device.deviceModel || device.deviceName || device.name || `Устройство ${index + 1}`}
                        </div>
                        <div className="device-status">
                          {device.platform ? (
                            <>
                              {device.platform}
                              {device.osVersion && ` ${device.osVersion}`}
                            </>
                          ) : (
                            'Активно'
                          )}
                        </div>
                        {device.updatedAt && (
                          <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.4)', marginTop: '0.25rem' }}>
                            Обновлено: {new Date(device.updatedAt).toLocaleDateString('ru-RU', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }

    if (activeTab === 'add') {
      return (
        <>
          <div className="dashboard-header">
            <div className="header-left">
              <button className="header-icon-btn" onClick={() => setActiveTab('home')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"></path>
                </svg>
              </button>
              <div className="header-title">
                <h1>Добавить устройство</h1>
                <p>NaixVPN</p>
              </div>
            </div>
          </div>
          <div className="add-device-section">
            <div className="add-device-form">
              <input
                type="text"
                className="device-name-input"
                placeholder="Название устройства"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
              />
              <button className="add-device-submit" onClick={handleAddDevice}>
                Добавить устройство
              </button>
            </div>
          </div>
        </>
      );
    }

    if (activeTab === 'install') {
      const subscriptionUrl = user?.shortUuid ? `https://naix.cc/sub/K2XgUge3N3lMnZdFTrS10qec/${user.shortUuid}` : '';
      
      return (
        <>
          <div className="dashboard-header">
            <div className="header-left">
              <button className="header-icon-btn" onClick={() => setActiveTab('home')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"></path>
                </svg>
              </button>
              <div className="header-title">
                <h1>Установка VPN</h1>
                <p>NaixVPN</p>
              </div>
            </div>
          </div>
          <div className="install-vpn-section">
            <div className="install-steps">
              <div className="install-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3 className="step-title">Скачайте Incy</h3>
                  <p className="step-description">
                    Скачайте приложение <strong>Incy</strong> для вашего устройства
                  </p>
                  <div className="step-buttons">
                    <a
                      href="https://apps.apple.com/us/app/incy/id6756943388"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="store-btn app-store-btn"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      App Store
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=llc.itdev.incy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="store-btn play-store-btn"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                      </svg>
                      Google Play
                    </a>
                    <a
                      href="https://github.com/INCY-DEV/incy-platforms/releases/latest/download/incy-windows-setup.exe"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="store-btn windows-btn"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,12V6.75L9,5.43V11.91L3,12M20,3V11.75L10,11.9V5.21L20,3M3,13L9,13.09V19.9L3,18.75V13M20,13.25V22L10,20.09V13.1L20,13.25Z"/>
                      </svg>
                      Windows
                    </a>
                  </div>
                </div>
              </div>

              <div className="install-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3 className="step-title">Импорт конфигурации</h3>
                  <p className="step-description">
                    Нажмите кнопку ниже для автоматического импорта подписки в Incy
                  </p>
                  <div className="config-buttons">
                    <a
                      href={subscriptionUrl ? `incy://add/${encodeURIComponent(subscriptionUrl)}` : '#'}
                      className="copy-config-btn import-btn primary-import-btn"
                      onClick={(e) => {
                        if (!subscriptionUrl) {
                          e.preventDefault();
                          alert('Ссылка подписки еще не готова');
                        }
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Автоматический импорт
                    </a>
                  </div>
                  <div className="subscription-url-section">
                    <p className="subscription-url-label">Ссылка для импорта подписки:</p>
                    <div className="subscription-link-wrapper">
                      <input
                        type="text"
                        readOnly
                        className="subscription-link-input"
                        value={subscriptionUrl || 'Загрузка...'}
                        id="subscription-link-import"
                      />
                      <button
                        className="subscription-copy-btn"
                        onClick={() => {
                          if (!subscriptionUrl) {
                            alert('Ссылка подписки еще не готова');
                            return;
                          }
                          navigator.clipboard.writeText(subscriptionUrl).then(() => {
                            alert('Ссылка скопирована!');
                          }).catch(() => {
                            const input = document.getElementById('subscription-link-import');
                            input.select();
                            document.execCommand('copy');
                            alert('Ссылка скопирована!');
                          });
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        Копировать
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="install-success">
              <div className="success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3 className="success-title">Готово к использованию!</h3>
              <p className="success-description">
                Нажмите кнопку включения в приложении Happ и пользуйтесь VPN
              </p>
            </div>

            <div className="manual-instructions-block">
              <button 
                className="manual-instructions-toggle"
                onClick={() => setShowManualInstructions(!showManualInstructions)}
              >
                <span>Если не удалось автоматически добавить</span>
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  className={showManualInstructions ? 'rotated' : ''}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              {showManualInstructions && (
                <div className="manual-instructions-content">
                  <div className="manual-step">
                    <div className="manual-step-number">1</div>
                    <div className="manual-step-text">
                      Откройте приложение Incy
                    </div>
                      </div>
                  <div className="manual-step">
                    <div className="manual-step-number">2</div>
                    <div className="manual-step-text">
                      Нажмите кнопку ниже, чтобы скопировать конфигурацию
                    </div>
                  </div>
                  <div className="manual-copy-section">
                    <div className="subscription-link-wrapper" style={{ marginBottom: '1rem' }}>
                      <input
                        type="text"
                        readOnly
                        className="subscription-link-input"
                        value={subscriptionUrl || 'Загрузка...'}
                        id="subscription-link-manual"
                      />
                      <button
                        className="subscription-copy-btn"
                        onClick={() => {
                          if (!subscriptionUrl) {
                            alert('Ссылка подписки еще не готова');
                            return;
                          }
                          const input = document.getElementById('subscription-link-manual');
                          input.select();
                          try {
                            document.execCommand('copy');
                            alert('Ссылка скопирована! Теперь перейдите в Incy и выполните следующие шаги.');
                          } catch (err) {
                            navigator.clipboard.writeText(subscriptionUrl).then(() => {
                              alert('Ссылка скопирована! Теперь перейдите в Incy и выполните следующие шаги.');
                            }).catch(() => {
                              alert('Не удалось скопировать ссылку');
                            });
                          }
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        Копировать
                      </button>
                    </div>
                  </div>
                  <div className="manual-step">
                    <div className="manual-step-number">3</div>
                    <div className="manual-step-text">
                      Нажмите кнопку <strong>"+"</strong> в правом верхнем углу
                    </div>
                  </div>
                  <div className="manual-step">
                    <div className="manual-step-number">4</div>
                    <div className="manual-step-text">
                      Выберите <strong>"Импорт из буфера обмена"</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>


          </div>
        </>
      );
    }

    if (activeTab === 'topup') {
      return (
        <>
          <div className="dashboard-header">
            <div className="header-left">
              <button className="header-icon-btn" onClick={() => setActiveTab('home')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"></path>
                </svg>
              </button>
              <div className="header-title">
                <h1>Пополнить баланс</h1>
                <p>NaixVPN</p>
              </div>
            </div>
          </div>
          <div className="topup-section">
            <div className="topup-amounts">
              <button className="topup-btn" onClick={() => handleTopUp(120)}>
                120 Р
              </button>
              <button className="topup-btn" onClick={() => handleTopUp(240)}>
                240 Р
              </button>
              <button className="topup-btn" onClick={() => handleTopUp(360)}>
                360 Р
              </button>
              <button className="topup-btn" onClick={() => handleTopUp(480)}>
                480 Р
              </button>
              <button className="topup-btn" onClick={() => handleTopUp(600)}>
                600 Р
              </button>
              <button className="topup-btn" onClick={() => handleTopUp(1500)}>
                1500 Р
              </button>
            </div>
            <div className="current-balance">
              <div className="balance-label">Текущий баланс</div>
              <div className="balance-amount">{balance.toFixed(2)} Р</div>
              </div>
          </div>
        </>
      );
    }

    if (activeTab === 'lte') {
      return (
        <>
          <div className="dashboard-header">
            <div className="header-left">
              <button className="header-icon-btn" onClick={() => setActiveTab('home')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"></path>
                </svg>
              </button>
              <div className="header-title">
                <h1>Купить LTE трафик</h1>
                <p>NaixVPN</p>
              </div>
            </div>
          </div>
          <div className="lte-section">
            <div className="lte-info-card">
              <h2 className="lte-title">Дополнительный LTE трафик</h2>
              <p className="lte-description">
                Покупайте дополнительный трафик для использования VPN через мобильные сети LTE.
                Трафик действует <strong>1 месяц</strong> с момента покупки.
              </p>
            </div>

            <div className="lte-current-stats">
              <div className="lte-stat-item">
                <div className="lte-stat-label">Доступно</div>
                <div className="lte-stat-value">{lteTrafficTotalGB} ГБ</div>
              </div>
              <div className="lte-stat-item">
                <div className="lte-stat-label">Использовано</div>
                <div className="lte-stat-value">{lteTrafficUsedGB.toFixed(2)} ГБ</div>
              </div>
              <div className="lte-stat-item">
                <div className="lte-stat-label">Осталось</div>
                <div className="lte-stat-value">{lteTrafficRemainingGB.toFixed(2)} ГБ</div>
              </div>
            </div>

            <div className="lte-plans">
              <h3 className="lte-plans-title">Выберите тариф</h3>
              <div className="lte-plans-grid">
                <button 
                  className="lte-plan-btn"
                  onClick={() => handlePurchaseLte(30)}
                  disabled={ltePurchaseLoading}
                >
                  <div className="lte-plan-amount">30 ГБ</div>
                  <div className="lte-plan-price">100 ₽</div>
                </button>
                <button 
                  className="lte-plan-btn lte-plan-popular"
                  onClick={() => handlePurchaseLte(60)}
                  disabled={ltePurchaseLoading}
                >
                  <div className="lte-plan-badge">Выгодно</div>
                  <div className="lte-plan-amount">60 ГБ</div>
                  <div className="lte-plan-price">199 ₽</div>
                </button>
                <button 
                  className="lte-plan-btn"
                  onClick={() => handlePurchaseLte(100)}
                  disabled={ltePurchaseLoading}
                >
                  <div className="lte-plan-amount">100 ГБ</div>
                  <div className="lte-plan-price">299 ₽</div>
                </button>
              </div>
            </div>

            <div className="current-balance" style={{ marginTop: '1.5rem' }}>
              <div className="balance-label">Ваш баланс</div>
              <div className="balance-amount">{balance.toFixed(2)} ₽</div>
            </div>

            {balance < 100 && (
              <button 
                className="lte-topup-btn"
                onClick={() => setActiveTab('topup')}
              >
                Пополнить баланс
              </button>
            )}
          </div>
        </>
      );
    }

    if (activeTab === 'referral') {
              return (
        <>
          <div className="dashboard-header">
            <div className="header-left">
              <button className="header-icon-btn" onClick={() => setActiveTab('home')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"></path>
                </svg>
              </button>
              <div className="header-title">
                <h1>Реферальная программа</h1>
                <p>NaixVPN</p>
              </div>
            </div>
          </div>
          <div className="referral-section">
            <div className="referral-info-card">
              <h2 className="referral-title">Приглашайте друзей и получайте бонусы!</h2>
              <p className="referral-description">
                За каждого друга, который зарегистрируется по вашей ссылке и совершит первое пополнение, 
                вы получите <strong>1 месяц бесплатного VPN</strong>.
              </p>
                  </div>
            
            <div className="referral-link-card">
              <h3 className="referral-subtitle">Ваша реферальная ссылка</h3>
              <div className="referral-link-wrapper">
                <input
                  type="text"
                  readOnly
                  className="referral-link-input"
                  value={user?.referralCode ? `${window.location.origin}/?ref=${user.referralCode}` : user?.id ? `${window.location.origin}/?ref=${user.id}` : 'Загрузка...'}
                  id="referral-link"
                />
                  <button
                  className="referral-copy-btn"
                  onClick={async () => {
                    const referralLink = user?.referralCode 
                      ? `${window.location.origin}/?ref=${user.referralCode}` 
                      : user?.id 
                        ? `${window.location.origin}/?ref=${user.id}` 
                        : '';
                    
                    if (!referralLink) {
                      alert('Реферальная ссылка еще не готова');
                      return;
                    }

                    try {
                      // Try modern clipboard API first (works on mobile)
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(referralLink);
                        alert('Ссылка скопирована!');
                      } else {
                        // Fallback for older browsers
                        const input = document.getElementById('referral-link');
                        input.select();
                        input.setSelectionRange(0, 99999); // For mobile devices
                        try {
                          document.execCommand('copy');
                          alert('Ссылка скопирована!');
                        } catch (err) {
                          // Last resort: create temporary textarea
                          const textArea = document.createElement('textarea');
                          textArea.value = referralLink;
                          textArea.style.position = 'fixed';
                          textArea.style.opacity = '0';
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          alert('Ссылка скопирована!');
                        }
                      }
                    } catch (err) {
                      console.error('Failed to copy:', err);
                      alert('Не удалось скопировать ссылку. Попробуйте выделить и скопировать вручную.');
                    }
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  Копировать
                  </button>
              </div>
            </div>

            <div className="referral-stats-card">
              <h3 className="referral-subtitle">Статистика</h3>
              <div className="referral-stats">
                <div className="referral-stat-item">
                  <div className="referral-stat-label">Приглашено друзей</div>
                  <div className="referral-stat-value">{user?.referralsCount || 0}</div>
                </div>
                <div className="referral-stat-item">
                  <div className="referral-stat-label">Получено месяцев</div>
                  <div className="referral-stat-value">{user?.referralMonths || 0}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }

    // Home tab (default)
    return (
      <>
        <div className="dashboard-header">
          <div className="header-left">
            <div className="header-title">
              <h1>Главная</h1>
              <p>NaixVPN</p>
            </div>
          </div>
          <button className="refresh-btn" onClick={handleRefresh}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            Обновить
          </button>
        </div>

        {error && <div className="dashboard-error">{error}</div>}

      {/* Profile Section */}
      <div className="profile-section">
        <h2 className="section-title">Профиль</h2>
        <div className="profile-items">
          <div className="profile-item">
            <div className="profile-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                <line x1="1" y1="10" x2="23" y2="10"></line>
              </svg>
            </div>
            <div className="profile-item-content">
              <div className="profile-item-label">Баланс</div>
              <div className="profile-item-value">{balance.toFixed(2)} Р</div>
            </div>
          </div>
          <div className="profile-item">
            <div className="profile-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="profile-item-content">
              <div className="profile-item-label">Тариф в сутки</div>
              <div className="profile-item-value">{dailyRate.toFixed(2)} Р</div>
            </div>
          </div>
          <div className="profile-item">
            <div className="profile-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 20h22"></path>
                <path d="M2 16h3v4H2z"></path>
                <path d="M7 12h3v8H7z"></path>
                <path d="M12 8h3v12h-3z"></path>
                <path d="M17 4h3v16h-3z"></path>
              </svg>
            </div>
            <div className="profile-item-content">
              <div className="profile-item-label">LTE трафик</div>
              <div className="profile-item-value">
                {lteTrafficUsedGB.toFixed(2)} / {lteTrafficTotalGB} ГБ
              </div>
            </div>
          </div>
          <div className="lte-traffic-bar">
            <div 
              className="lte-traffic-progress" 
              style={{ 
                width: `${Math.min(100, (lteTrafficUsedGB / lteTrafficTotalGB) * 100)}%`,
                backgroundColor: lteTrafficUsedGB >= lteTrafficTotalGB ? '#ef4444' : 
                                 lteTrafficUsedGB >= lteTrafficTotalGB * 0.8 ? '#f59e0b' : '#22c55e'
              }}
            ></div>
          </div>
          <div className="lte-traffic-info">
            <span>Осталось: {lteTrafficRemainingGB.toFixed(2)} ГБ</span>
            <button className="lte-buy-link" onClick={() => setActiveTab('lte')}>
              Купить ещё
            </button>
          </div>
          <button className="profile-topup-btn" onClick={() => setActiveTab('topup')}>
            <div className="profile-item-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                <line x1="1" y1="10" x2="23" y2="10"></line>
              </svg>
            </div>
            <div className="profile-item-content">
              <div className="profile-item-label">Пополнить баланс</div>
            </div>
            <div className="profile-topup-arrow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </button>
        </div>
        {(() => {
          // Check if user is on trial period (balance is 4 rubles and less than 24 hours since registration)
          const isTrialPeriod = Math.abs(balance - 4) < 0.01 && user?.createdAt && (new Date() - new Date(user.createdAt)) < 24 * 60 * 60 * 1000;
          
          if (isTrialPeriod) {
            return (
              <div className="balance-warning balance-warning-trial" style={{ backgroundColor: '#e3f2fd', color: '#1976d2', border: '1px solid #90caf9' }}>
                🎁 Вам выдан пробный период на один день
              </div>
            );
          }
          
          const daysLeft = balance / dailyRate;
          if (daysLeft < 1) {
            return (
              <div className="balance-warning balance-warning-critical">
                Срок действия подписки истек, пополните баланс чтобы дальше использовать VPN
              </div>
            );
          } else if (daysLeft < 5) {
            return (
              <div className="balance-warning balance-warning-low">
                VPN будет работать еще {Math.floor(daysLeft)} {Math.floor(daysLeft) === 1 ? 'день' : Math.floor(daysLeft) < 5 ? 'дня' : 'дней'}
              </div>
            );
          } else {
            return (
              <div className="balance-warning balance-warning-ok">
                VPN будет работать еще {Math.floor(daysLeft)} {Math.floor(daysLeft) === 1 ? 'день' : Math.floor(daysLeft) < 5 ? 'дня' : 'дней'}
              </div>
            );
          }
        })()}
      </div>

      {/* VPN Buttons */}
      <div className="vpn-buttons-section">
        <button className="install-vpn-btn" onClick={() => setActiveTab('install')}>
          <svg className="vpn-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <span>Установить VPN</span>
        </button>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h2 className="section-title">Быстрые действия</h2>
        <div className="quick-actions-grid">
          <button className="quick-action-btn" onClick={() => setActiveTab('devices')}>
            <svg className="action-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
            </svg>
            <span>Мои устройства</span>
          </button>
          <button className="quick-action-btn" onClick={() => setActiveTab('referral')}>
            <svg className="action-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <path d="M20 8v6M23 11h-6"></path>
            </svg>
            <span>Пригласить друга</span>
          </button>
          <button className="quick-action-btn" onClick={() => setShowSupportDialog(true)}>
            <svg className="action-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              <path d="M13 8H7"></path>
              <path d="M17 12H7"></path>
            </svg>
            <span>Техническая поддержка</span>
          </button>
        </div>
      </div>
      </>
    );
  };

  const loadSupportMessages = async () => {
    try {
      const response = await axios.get('/api/support/messages');
      const messages = response.data.messages || [];
      console.log('Loaded support messages:', messages);
      // Log messages with media
      messages.forEach(msg => {
        if (msg.mediaType && msg.filePath) {
          console.log(`Message ${msg.id}: mediaType=${msg.mediaType}, filePath=${msg.filePath}, fromUser=${msg.fromUser}`);
        }
      });
      setSupportMessages(messages);
      
      // Scroll to bottom after messages are loaded
      setTimeout(() => {
        if (supportMessagesEndRef.current) {
          supportMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      console.error('Error loading support messages:', error);
    }
  };

  const sendSupportMessage = async () => {
    if (!supportMessage.trim() && !supportFile) return;
    
    setSupportLoading(true);
    try {
      const formData = new FormData();
      if (supportMessage.trim()) {
        formData.append('message', supportMessage);
      }
      if (supportFile) {
        formData.append('file', supportFile);
      }

      const response = await axios.post('/api/support/send', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSupportMessages([...supportMessages, response.data.message]);
      setSupportMessage('');
      setSupportFile(null);
      setSupportFilePreview(null);
      
      // Scroll to bottom after sending message
      setTimeout(() => {
        if (supportMessagesEndRef.current) {
          supportMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      alert('Ошибка отправки сообщения');
    } finally {
      setSupportLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        alert('Пожалуйста, выберите изображение или видео');
        return;
      }
      
      // Check file size (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        alert('Файл слишком большой. Максимальный размер: 20MB');
        return;
      }

      setSupportFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setSupportFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setSupportFilePreview(null);
      }
    }
  };

  const removeFile = () => {
    setSupportFile(null);
    setSupportFilePreview(null);
  };

  return (
  <div className="dashboard-mobile">
    <main className="dashboard-content">
      {renderContent()}
    </main>
      
      {showSupportDialog && (
        <div className="support-dialog-overlay" onClick={() => setShowSupportDialog(false)}>
          <div className="support-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="support-dialog-header">
              <h2>Техническая поддержка</h2>
              <button 
                className="support-dialog-close"
                onClick={() => setShowSupportDialog(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div className="support-messages">
              {supportMessages.length === 0 ? (
                <div className="support-empty">
                  <p>Начните диалог с поддержкой</p>
                </div>
              ) : (
                <>
                  {supportMessages.map((msg, index) => (
                    <div 
                      key={index} 
                      className={`support-message ${msg.fromUser ? 'support-message-user' : 'support-message-support'}`}
                    >
                    {msg.mediaType && (
                      <div className="support-message-media">
                        {msg.fromUser ? (
                          // For user messages, just show text
                          <div className="support-media-placeholder">
                            {msg.mediaType === 'photo' ? '📷 Вы отправили фото' : '🎥 Вы отправили видео'}
                          </div>
                        ) : (
                          // For support messages, show actual media
                          <>
                            {msg.mediaType === 'photo' && (
                              msg.filePath ? (
                                <img 
                                  src={msg.filePath}
                                  alt="Photo"
                                  className="support-media-image"
                                  onError={(e) => {
                                    console.error('Error loading image:', msg.filePath, e);
                                    e.target.style.display = 'none';
                                  }}
                                  onLoad={() => {
                                    console.log('Image loaded successfully:', msg.filePath);
                                  }}
                                />
                              ) : (
                                <div className="support-media-placeholder">Фото не загружено (filePath отсутствует)</div>
                              )
                            )}
                            {msg.mediaType === 'video' && (
                              msg.filePath ? (
                                <video 
                                  src={msg.filePath}
                                  controls
                                  className="support-media-video-element"
                                  onError={(e) => {
                                    console.error('Error loading video:', msg.filePath, e);
                                  }}
                                >
                                  Ваш браузер не поддерживает видео.
                                </video>
                              ) : (
                                <div className="support-media-placeholder">Видео не загружено (filePath отсутствует)</div>
                              )
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {msg.text && (
                      <div className="support-message-content">{msg.text}</div>
                    )}
                    <div className="support-message-time">
                      {new Date(msg.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  ))}
                  <div ref={supportMessagesEndRef} />
                </>
              )}
            </div>
            
            {supportFilePreview && (
              <div className="support-file-preview">
                <img src={supportFilePreview} alt="Preview" className="support-file-preview-image" />
                <button className="support-file-remove" onClick={removeFile}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            )}
            
            {supportFile && !supportFilePreview && (
              <div className="support-file-info">
                <span>{supportFile.name}</span>
                <button className="support-file-remove-small" onClick={removeFile}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            )}

            <div className="support-input-area">
              <label className="support-file-btn">
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  disabled={supportLoading}
                  style={{ display: 'none' }}
                />
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
              </label>
              <input
                type="text"
                className="support-input"
                placeholder="Введите ваше сообщение..."
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendSupportMessage()}
                disabled={supportLoading}
              />
              <button 
                className="support-send-btn"
                onClick={sendSupportMessage}
                disabled={supportLoading || (!supportMessage.trim() && !supportFile)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    <footer className="legal-footer-jyk">
      <div className="legal-info-jyk">
        <p>STIX LIMITED LIABILITY COMPANY (LLC)</p>
        <p>Рег.номер 278.110.1445669</p>
        <p>165 V. Atshemyan str., Erebuni 0005</p>
        <p>Yerevan, Armenia</p>
        <p className="legal-links-jyk">
          <Link to="/auto-renewal">Отключить автосписания</Link>
          {' · '}
          <a href="http://naix.cc/refund.html" target="_blank" rel="noopener noreferrer">Политика возврата</a>
          {' · '}
          <a href="https://naix.cc/privacy.html" target="_blank" rel="noopener noreferrer">Политика конфиденциальности</a>
          {' · '}
          <a href="https://naix.cc/oferta.html" target="_blank" rel="noopener noreferrer">Публичная оферта</a>
        </p>
      </div>
    </footer>


    </div>
  );
};

export default Dashboard;
